<!--
	Comment form: shows the prefilled `draft` location and submits a free-text comment
	through the issueReporter client with the active API key.
-->
<script lang="ts">
	import Modal from '#lib/components/Modal.svelte';
	import AbleyButton from '#lib/components/AbleyButton.svelte';
	import InfoButton from '#lib/components/InfoButton.svelte';
	import Alert from '#lib/components/Alert.svelte';
	import { apiKeyStore } from '#lib/stores/apiKeyStore.svelte.js';
	import { submitReport, IssueReporterError } from './issueReporterClient';
	import type { ReportDraft } from './buildReport';
	import { commentFlagStore } from './commentFlagStore.svelte';
	import { getLogger } from '@logtape/logtape';

	const log = getLogger(['platform', 'comment-flags']);

	interface Props {
		open: boolean;
		draft: ReportDraft;
		onclose: () => void;
	}

	const { open, draft, onclose }: Props = $props();

	type Status = 'idle' | 'submitting' | 'success' | 'error';
	let freetext = $state('');
	let status = $state<Status>('idle');
	let errorMessage = $state('');

	let successEl: HTMLParagraphElement | undefined = $state();

	// The Submit button disappears on success, so move focus to the confirmation instead of <body>.
	$effect(() => {
		if (status === 'success') successEl?.focus();
	});

	function close() {
		// Reset transient state so the next open starts clean.
		freetext = '';
		status = 'idle';
		errorMessage = '';
		onclose();
	}

	async function handleSubmit(event: SubmitEvent) {
		event.preventDefault();
		if (status === 'submitting' || !freetext.trim()) return;
		const apiKey = apiKeyStore.apiKey;
		if (!apiKey) {
			status = 'error';
			errorMessage = "Your session key isn't available — please reload and sign in again.";
			return;
		}
		status = 'submitting';
		errorMessage = '';
		try {
			await submitReport(
				{
					lat: draft.lat,
					lon: draft.lon,
					source: draft.source,
					freetext: freetext.trim(),
					location_info: draft.locationInfo,
					geometry: draft.geometry
				},
				{ apiKey }
			);
			// Drop a persistent local flag at the commented feature. Best-effort:
			// the comment already succeeded server-side, so a flag failure must not
			// turn this into an error. Only one category exists for now.
			try {
				commentFlagStore.add({
					id: crypto.randomUUID(),
					app: draft.source,
					lon: draft.lon,
					lat: draft.lat,
					featureId: draft.locationInfo.featureId,
					layerId: draft.locationInfo.layerId,
					systemId: draft.locationInfo.systemId,
					label: draft.locationInfo.context?.area ?? draft.positionLabel,
					comment: freetext.trim(),
					category: 'Data Issue',
					createdAt: new Date().toISOString()
				});
			} catch (error) {
				// flag drop is best-effort — the comment already succeeded server-side
				log.warn('Could not drop comment flag: {error}', { error });
			}
			status = 'success';
		} catch (err) {
			status = 'error';
			if (err instanceof IssueReporterError) {
				if (err.status === 401) {
					errorMessage = "Your session key isn't valid — please reload and sign in again.";
				} else if (err.status === 403) {
					errorMessage = "This comment couldn't be submitted from here.";
				} else {
					errorMessage = 'Something went wrong submitting your comment. Please try again.';
				}
			} else {
				errorMessage =
					"Couldn't reach the reporter service. Please check your connection and try again.";
			}
		}
	}
</script>

<Modal {open} title="Add comment" onclose={close}>
	{#if status === 'success'}
		<p
			class="successText"
			data-testid="report-success"
			role="status"
			tabindex="-1"
			bind:this={successEl}>
			Thanks, your comment has been submitted to Abley for review. We've also added it to the map
			(visible when zoomed in) for your future reference.
		</p>
	{:else}
		<form id="reportForm" class="reportForm" onsubmit={handleSubmit}>
			<div class="locationRow">
				<span class="overline">Location</span>
				<span class="locationValue" data-testid="report-location">{draft.positionLabel}</span>
				<InfoButton
					content="The location and feature details shown here are captured automatically from the feature you selected and sent with your comment." />
			</div>

			<label class="formField">
				<span class="formLabel">Your comment</span>
				<textarea
					id="report-freetext"
					class="freetext"
					data-testid="report-freetext"
					bind:value={freetext}
					required
					rows="5"
					placeholder="Enter description"
					aria-describedby="reportVisibility"></textarea>
			</label>

			<p id="reportVisibility" class="hint">
				Your comment will be submitted to Abley for review and will be visible on the map for your
				future reference.
			</p>

			{#if status === 'error'}
				<div data-testid="report-error"><Alert>{errorMessage}</Alert></div>
			{/if}
		</form>
	{/if}

	{#snippet footer()}
		{#if status === 'success'}
			<AbleyButton onclick={close}>Close</AbleyButton>
		{:else}
			<AbleyButton variant="outlined" onclick={close}>Cancel</AbleyButton>
			<AbleyButton type="submit" form="reportForm" loading={status === 'submitting'}>
				Submit
			</AbleyButton>
		{/if}
	{/snippet}
</Modal>

<style>
	.reportForm {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.locationRow {
		display: flex;
		align-items: center;
		gap: var(--space-2);

		.locationValue {
			font-variant-numeric: tabular-nums;
		}
	}

	.freetext {
		field-sizing: content;
		min-block-size: 6lh;
		max-block-size: 16lh;
	}

	.successText {
		font-size: var(--font-size-lg);

		&:focus {
			outline: none;
		}
	}
</style>
